import { useTranslation } from "react-i18next";
import { UserIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { IEvaluationRequestDetailPerson } from "@/types/(company)/performance.interface";

interface PerformanceReviewHeaderProps {
  employee: IEvaluationRequestDetailPerson;
  evaluatedBy: IEvaluationRequestDetailPerson;
  roleName: string;
  isReviewMode?: boolean;
  team: {
    name: string;
    image: string;
  };
}

export const PerformanceReviewHeader = ({
  employee,
  evaluatedBy,
  roleName,
  isReviewMode,
  team,
}: PerformanceReviewHeaderProps) => {
  const { t } = useTranslation(["common", "company"]);

  return (
    <div className="flex items-center justify-between px-5 py-3 bg-info-muted border-b border-border">
      {/* Employee info */}
      <div className="flex items-center gap-3">
        <Avatar className="rounded-xxs shrink-0 border border-white">
          <AvatarImage
            src={employee?.profileImage ?? undefined}
            className="rounded-xxs object-cover"
          />
          <AvatarFallback className="rounded-xxs bg-border">
            <UserIcon size={14} className="text-gray-500" />
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="text-xs text-gray-500">
            {t("company:performance.role_label", { role: roleName })}
          </p>
          <p className="text-sm font-semibold text-gray-800">
            {employee.firstName + " " + employee.lastName}
          </p>
        </div>
      </div>

      {/* Evaluated by */}
      {!isReviewMode ? (
        <div className="flex items-center gap-3">
          <div className="text-end">
            <p className="text-xs text-gray-500">
              {t("company:performance_review.evaluated_by_label")}
            </p>
            <p className="text-sm font-semibold text-gray-800">
              {evaluatedBy?.firstName + " " + evaluatedBy?.lastName}
            </p>
          </div>
          <Avatar className="h-10 w-10 rounded-xxs shrink-0 border border-white">
            <AvatarImage
              src={evaluatedBy?.profileImage ?? undefined}
              className="rounded-xxs object-cover"
            />
            <AvatarFallback className="rounded-xxs bg-border">
              <UserIcon size={14} className="text-gray-500" />
            </AvatarFallback>
          </Avatar>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 rounded-xxs shrink-0 border border-white">
            <AvatarImage
              src={team?.image ?? undefined}
              className="rounded-xxs object-cover"
            />
            <AvatarFallback className="rounded-xxs bg-border">
              <UserIcon size={14} className="text-gray-500" />
            </AvatarFallback>
          </Avatar>
          <div className="text-start">
            <p className="text-xs uppercase text-gray-500">
              {t("company:performance_review.team_label")}
            </p>
            <p className="text-sm font-semibold text-gray-800">
              {team?.name ?? "-"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
